import { Button } from "@ameelio/ui";
import { useLazyQuery } from "@apollo/client/react";
import Close from "@mui/icons-material/Close";
import Download from "@mui/icons-material/Download";
import { Avatar, Box, Dialog, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentCorrespondent } from "../SessionBoundary";
import { GetMessageDocument } from "./GetMessage.generated";

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  messageId: string;
};

export default function ZoomableImage({ src, messageId, ...rest }: Props) {
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const user = useCurrentCorrespondent();
  const { t } = useTranslation();

  const [refetchUrlAndDownload] = useLazyQuery(GetMessageDocument, {
    fetchPolicy: "network-only",
  });

  const downloadImage = async () => {
    const { data } = await refetchUrlAndDownload({
      variables: { id: messageId },
    });
    const url = data?.message?.fileUrl;

    if (url) {
      const downloadLink = document.createElement("a");
      downloadLink.href = url;

      document.body.appendChild(downloadLink);

      downloadLink.click();

      document.body.removeChild(downloadLink);
    }
  };

  return (
    <>
      <Box
        onClick={() => setOpenImageDialog(true)}
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src={src}
          alt={t("Something went wrong loading the image for this message")}
          {...rest}
        />
      </Box>
      <Dialog
        fullScreen
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        slotProps={{
          paper: {
            style: {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          },
        }}
      >
        <Stack style={{ maxHeight: "100%" }}>
          <Box
            sx={{
              display: "flex",
              position: "absolute",
              height: "80px",
              right: "4px",
              top: 0,
            }}
          >
            {user.__typename !== "Inmate" && (
              <Button onClick={downloadImage}>
                <Avatar sx={{ bgcolor: "white", color: "text.primary" }}>
                  <Download />
                </Avatar>
              </Button>
            )}
            <Button onClick={() => setOpenImageDialog(false)}>
              <Avatar sx={{ bgcolor: "white", color: "text.primary" }}>
                <Close />
              </Avatar>
            </Button>
          </Box>
          <Box
            sx={{
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              height: "100%",
              py: 10,
            }}
          >
            <img
              src={src}
              alt={t("Something went wrong loading the image for this message")}
              style={{ maxHeight: "100%" }}
            />
          </Box>
          <Box
            sx={{
              alignItems: "right",
              height: (th) => th.spacing(8),
            }}
          />
        </Stack>
      </Dialog>
    </>
  );
}
